import { useRouter } from 'next/router'
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useAnalytics } from '../analytics'
import useDeviceDetect from '../hooks/useDeviceDetect'
import axios from 'axios'

const CHATBOT_BLACKLIST_PAGES = ['/es/espanol']

const defaultCannedQuestions = [
  'How much could this program save me?',
  'How does Freedom Debt Relief work?',
  'How much does the program cost?',
  'Am I eligible for this program?',
]

export const ChatbotContext = createContext({})

const ChatbotProvider = React.memo(({ children }) => {
  const { pathname } = useRouter()
  const [isChatbotOpen, setIsChatbotOpen] = useState(false)
  const [isChatbotActivated, setIsChatbotActivated] = useState(false)
  const [cannedQuestions, setCannedQuestions] = useState([])
  const { track } = useAnalytics()
  const { isMobile } = useDeviceDetect()

  const openChatbot = () => {
    setIsChatbotActivated(true)
    setIsChatbotOpen(true)
    track({
      list_name: 'AIChatbot_Click',
      event_type: 'track',
      event_name: 'AIChatbot_Click',
      event_action: 'AIChatbot_Click',
      nav_link_section: 'FDR Chatbot',
      track_event: 'chat_experiment_start',
      click_type: 'button_click',
      click_text: 'Chat with us',
      click_id: 'Chat with us',
    })
  }

  const closeChatbot = () => setIsChatbotOpen(false)

  const isChatbotEnabled = useMemo(
    () => !CHATBOT_BLACKLIST_PAGES.includes(pathname),
    [pathname]
  )

  useEffect(() => {
    if (isChatbotOpen && isMobile) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [isChatbotOpen, isMobile])

  useEffect(() => {
    if (!isChatbotActivated) return undefined

    const getCannedQuestions = async () => {
      try {
        const { data } = await axios.get('/api/chatbot-canned-questions')
        setCannedQuestions(data.data)
      } catch (error) {
        console.error(error)
        setCannedQuestions(defaultCannedQuestions)
      }
    }

    getCannedQuestions()
  }, [isChatbotActivated])

  return (
    <ChatbotContext.Provider
      value={{
        isChatbotOpen,
        isChatbotActivated,
        openChatbot,
        closeChatbot,
        isChatbotEnabled,
        cannedQuestions,
      }}
    >
      {children}
    </ChatbotContext.Provider>
  )
})

const useChatbot = () => {
  const context = useContext(ChatbotContext)

  return context
}

ChatbotProvider.displayName = 'ChatbotProvider'

export { ChatbotProvider, useChatbot }
